import React from 'react';
import { Flex, Radio, Switch, Typography } from 'antd';
import { Divider } from '@/shared/ui/Divider';
import {
  Settings,
  CineSpeed,
  PanelLayout,
  CINE_SPEEDS,
} from '@/models/settings';
import { GridLayoutPicker } from './GridLayoutPicker';
import styles from './ViewerSection.module.css';

const { Text } = Typography;

interface ViewerSectionProps {
  settings: Settings;
  onUpdate: (partial: Partial<Settings>) => void;
}

function parsePanelLayout(layout: PanelLayout) {
  const [rows, cols] = layout.split('x').map(Number);
  return { rows, cols };
}

export const ViewerSection: React.FC<ViewerSectionProps> = ({
  settings,
  onUpdate,
}) => (
  <div className={styles.rows}>
    <Flex
      id="collapsed-assignments"
      align="center"
      justify="space-between"
      className={styles.row}
    >
      <Flex vertical>
        <Text>Collapsed assignments</Text>
        <Text type="secondary" className={styles.hint}>
          Collapse the assignments panel by default.
        </Text>
      </Flex>
      <Switch
        checked={settings.collapsedAssignments}
        onChange={(checked) => onUpdate({ collapsedAssignments: checked })}
      />
    </Flex>

    <Divider />

    <Flex
      id="popped-out-viewer"
      align="center"
      justify="space-between"
      className={styles.row}
    >
      <Flex vertical>
        <Text>Popped out viewer</Text>
        <Text type="secondary" className={styles.hint}>
          Open the viewer in a separate window by default.
        </Text>
      </Flex>
      <Switch
        checked={settings.poppedOutViewer}
        onChange={(checked) => onUpdate({ poppedOutViewer: checked })}
      />
    </Flex>

    <Divider />

    <Flex
      id="panel-layout"
      align="center"
      justify="space-between"
      className={styles.row}
    >
      <Flex vertical>
        <Text>Panel layout</Text>
        <Text type="secondary" className={styles.hint}>
          Default grid arrangement for viewer panels.
        </Text>
      </Flex>
      <GridLayoutPicker
        value={parsePanelLayout(settings.panelLayout)}
        onChange={({ rows, cols }) =>
          onUpdate({ panelLayout: `${rows}x${cols}` as PanelLayout })
        }
      />
    </Flex>

    <Divider />

    <Flex
      id="collapsed-context-section"
      align="center"
      justify="space-between"
      className={styles.row}
    >
      <Flex vertical>
        <Text>Collapsed context section</Text>
        <Text type="secondary" className={styles.hint}>
          Collapse the context section by default.
        </Text>
      </Flex>
      <Switch
        checked={settings.collapsedContentSection}
        onChange={(checked) => onUpdate({ collapsedContentSection: checked })}
      />
    </Flex>

    <Divider />

    <Flex
      id="collapsed-measurement-section"
      align="center"
      justify="space-between"
      className={styles.row}
    >
      <Flex vertical>
        <Text>Collapsed measurement section</Text>
        <Text type="secondary" className={styles.hint}>
          Collapse the measurement section by default.
        </Text>
      </Flex>
      <Switch
        checked={settings.collapsedMeasurementSection}
        onChange={(checked) =>
          onUpdate({ collapsedMeasurementSection: checked })
        }
      />
    </Flex>

    <Divider />

    <Flex
      id="autoplay"
      align="center"
      justify="space-between"
      className={styles.row}
    >
      <Flex vertical>
        <Text>Autoplay</Text>
        <Text type="secondary" className={styles.hint}>
          Automatically play videos when loaded in the viewer.
        </Text>
      </Flex>
      <Switch
        checked={settings.autoplay}
        onChange={(checked) => onUpdate({ autoplay: checked })}
      />
    </Flex>

    <Divider />

    <Flex
      id="cine-speed"
      align="center"
      justify="space-between"
      className={styles.row}
    >
      <Flex vertical>
        <Text>Cine speed</Text>
        <Text type="secondary" className={styles.hint}>
          Playback speed for cine loops.
        </Text>
      </Flex>
      <Radio.Group
        value={settings.cineSpeed}
        onChange={(e) => onUpdate({ cineSpeed: e.target.value as CineSpeed })}
      >
        {CINE_SPEEDS.map((opt) => (
          <Radio.Button key={opt} value={opt}>
            {opt}
          </Radio.Button>
        ))}
      </Radio.Group>
    </Flex>
  </div>
);
